import React, { useState, useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles'
import { CssBaseline } from '@mui/material'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { AppSettingsProvider } from './contexts/AppSettingsContext'
import { useAppTheme } from './hooks/useAppTheme'
import { useDocumentTitle } from './hooks/useDocumentTitle'
import ProtectedRoute from './components/auth/ProtectedRoute'
import AuthContainer from './components/auth/AuthContainer'
import DashboardLayout from './components/layout/DashboardLayout'
import ProfilePage from './pages/ProfilePage'
import StudiesPage from './pages/StudiesPage'
// PatientsPage removed - functionality moved to Worklist
import PatientDetailsPage from './pages/PatientDetailsPage'
// ReportsPage removed - reports available in Worklist and Patient page
// DocumentsPage removed - functionality moved to Worklist
// TemplatesPage removed - templates now managed in ProfilePage
import ShiftsPage from './pages/ShiftsPage'
import TechniciansSchedulePage from './pages/TechniciansSchedulePage'
import ReceptionPage from './pages/ReceptionPage'
// Unified pages with tabs
import BillingPage from './pages/BillingPage'
import AnalyticsPage from './pages/AnalyticsPage'
import AdminPage from './pages/AdminPage'
import ConfigurationPage from './pages/ConfigurationPage'
import { BodyPartsManagement } from './components/BodyParts'
import SessionWarning from './components/auth/SessionWarning'
import { useSession } from './hooks/useSession'
import { useVersionCheck } from './hooks/useVersionCheck'
import { adminApi } from './services/api'

const AppContent: React.FC = () => {
  const { isAuthenticated, rememberMe } = useAuth()
  const [showSessionWarning, setShowSessionWarning] = useState(false)
  
  // ✨ VERSION CHECK: Auto-reload la versiune nouă
  useVersionCheck()
  
  // ✨ ENTERPRISE SESSION: Timeout bazat pe preferința utilizatorului
  // Cu remember_me: 7 zile (10080 minute) | Fără: 8 ore (480 minute)
  const [sessionConfig, setSessionConfig] = useState<{
    defaultTimeout: number
    rememberMeTimeout: number
    warningMinutes: number
  }>({
    defaultTimeout: 480,     // 8 ore default (fără remember_me) - o tură de lucru
    rememberMeTimeout: 10080, // 7 zile (cu remember_me)
    warningMinutes: 5        // Warning cu 5 minute înainte
  })
  
  // Actualizează titlul paginii dinamic
  useDocumentTitle()

  // ✨ REMEMBER ME: Încarcă config-ul din endpoint public (nu necesită autentificare)
  useEffect(() => {
    // Încarcă config-ul la start (o singură dată)
    fetch('/api/v1/public/session-config')
      .then(res => res.json())
      .then((config) => {
        if (config.default_timeout_minutes && config.remember_me_timeout_minutes) {
          setSessionConfig({
            defaultTimeout: config.default_timeout_minutes,
            rememberMeTimeout: config.remember_me_timeout_minutes,
            warningMinutes: config.warning_minutes || 2
          })
        }
      })
      .catch((error) => {
        console.warn('Nu s-au putut încărca setările de sesiune:', error)
        // Folosește default-urile setate mai sus
      })
  }, [])

  // ✨ REMEMBER ME: Calculează timeout-ul bazat pe preferință
  const sessionTimeoutMinutes = rememberMe 
    ? sessionConfig.rememberMeTimeout 
    : sessionConfig.defaultTimeout

  const { extendSession } = useSession({
    timeoutMinutes: sessionTimeoutMinutes,
    warningMinutes: sessionConfig.warningMinutes,
    onWarning: () => setShowSessionWarning(true),
    onTimeout: () => {
      // Logout automat la expirarea sesiunii
    }
  })

  const handleExtendSession = () => {
    extendSession()
    setShowSessionWarning(false)
  }

  const handleLogout = () => {
    setShowSessionWarning(false)
    // Logout-ul se va face automat prin useSession
  }

  return (
    <>
      <Routes>
        {/* Ruta de bază - worklist este pagina principală */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <StudiesPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* Dashboard removed - redirect for compatibility */}
        <Route path="/dashboard" element={<Navigate to="/" replace />} />
        <Route path="/dashboard/*" element={<Navigate to="/" replace />} />

        {/* Rute de autentificare - accesibile doar utilizatorilor neautentificați */}
        <Route 
          path="/login" 
          element={
            isAuthenticated ? (
              <Navigate to="/" replace />
            ) : (
              <AuthContainer />
            )
          } 
        />

        <Route 
          path="/forgot-password" 
          element={
            isAuthenticated ? (
              <Navigate to="/" replace />
            ) : (
              <AuthContainer />
            )
          } 
        />

        <Route 
          path="/reset-password" 
          element={
            isAuthenticated ? (
              <Navigate to="/" replace />
            ) : (
              <AuthContainer />
            )
          } 
        />

        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ProfilePage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />



        {/* Patients page removed - functionality moved to Worklist */}
        <Route path="/patients" element={<Navigate to="/" replace />} />
        <Route
          path="/patients/:patientId"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <PatientDetailsPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* Redirect pentru compatibilitate - vechiul URL /worklist și /studies */}
        <Route
          path="/worklist"
          element={<Navigate to="/" replace />}
        />
        <Route
          path="/worklist/*"
          element={<Navigate to="/" replace />}
        />
        <Route
          path="/studies"
          element={<Navigate to="/" replace />}
        />
        <Route
          path="/studies/*"
          element={<Navigate to="/" replace />}
        />

        {/* Reports page removed - reports available in Worklist and Patient page */}
        <Route path="/reports" element={<Navigate to="/" replace />} />
        <Route path="/reports/:id" element={<Navigate to="/" replace />} />
        
        {/* Templates route removed - templates now managed in ProfilePage */}

        {/* Documents page removed - functionality moved to Worklist */}
        <Route path="/documents" element={<Navigate to="/" replace />} />

        {/* Programator - Gărzi Medici */}
        <Route
          path="/programator"
          element={<Navigate to="/programator/garzi" replace />}
        />
        <Route
          path="/programator/garzi"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ShiftsPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        
        {/* Programator - Ture Tehnicieni */}
        <Route
          path="/programator/ture"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <TechniciansSchedulePage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        {/* Redirect vechi URL pentru compatibilitate */}
        <Route path="/programator/tehnicieni" element={<Navigate to="/programator/ture" replace />} />
        
        {/* Recepție - Programări Pacienți */}
        <Route
          path="/receptie"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ReceptionPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* Unified analytics page with tabs - admin only */}
        <Route
          path="/analytics/*"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <DashboardLayout>
                <AnalyticsPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* Configuration page - Instituții și Utilizatori */}
        <Route
          path="/configurare/*"
          element={
            <ProtectedRoute allowedRoles={['admin', 'operator']}>
              <DashboardLayout>
                <ConfigurationPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* Unified admin page with tabs */}
        <Route
          path="/admin/*"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <AdminPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* Unified billing page with tabs */}
        <Route
          path="/billing/*"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <BillingPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/body-parts"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <BodyPartsManagement />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        
        {/* Rute de compatibilitate pentru vechile URL-uri */}
        <Route
          path="/admin/validare-plati"
          element={<Navigate to="/billing" replace />}
        />

        <Route
          path="/admin/contracte"
          element={<Navigate to="/billing" replace />}
        />

        <Route
          path="/facturare"
          element={<Navigate to="/billing" replace />}
        />

        <Route
          path="/facturare/*"
          element={<Navigate to="/billing" replace />}
        />

        {/* Ruta pentru pagini inexistente */}
        <Route
          path="*"
          element={
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '100vh',
              flexDirection: 'column',
              gap: '1rem'
            }}>
              <h1>404 - Pagina nu a fost găsită</h1>
              <p>Pagina pe care o căutați nu există.</p>
            </div>
          }
        />
      </Routes>

      {/* Dialog pentru warning-ul de sesiune */}
      <SessionWarning
        open={showSessionWarning}
        onExtend={handleExtendSession}
        onLogout={handleLogout}
        warningMinutes={2}
      />
    </>
  )
}

const AppWithTheme: React.FC = () => {
  const theme = useAppTheme()

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <AppSettingsProvider>
          <AppContent />
        </AppSettingsProvider>
      </AuthProvider>
    </MuiThemeProvider>
  )
}

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <AppWithTheme />
    </ThemeProvider>
  )
}

export default App
